/**
 * Created Sprint: Sprint 1 - Monorepo Skeleton
 * Last contract-affecting Sprint: 打磨輪 · 棒 31-225(§11.2;zero FCE — readings/p13.txt)— 頁面的語言標記跟著介面語言
 *   (OBS-286 · Owner 2026-10-10 ①): a `languageChanged` listener registered BEFORE init() sets <html lang> to the
 *   language the UI actually renders in (resolvedLanguage; outside SUPPORTED_LOCALES ⇒ "en"), on the init-time change
 *   and on every later changeLanguage(); translations · detection · namespaces · the init call are unchanged.
 *   Prior: Sprint 38 - additive `flashcards` namespace (建卡 UI —
 *   nav / create modal / deck page / card browser / batch / toast strings; string-literal
 *   key like `calendar`/`stamps`, not in frozen I18N_NS). Prior: Sprint 34 - additive `calendar` namespace (曆法輪 —
 *   副曆註記: secondary-calendar / era / 六曜 / ISO-week annotation strings; string-literal
 *   key like `stamps`/`agenda`, not in frozen I18N_NS). Prior: Sprint 34 - additive
 *   `stamps` namespace (calendar スタンプ picker + 20 三語 icon names; string-literal key
 *   like `agenda`/`admin`, not in frozen I18N_NS). Prior: Sprint 31 批次 2 - additive
 *   `agenda` namespace (agenda
 *   memo quick-add; string-literal key like `import-export`/`admin`, not in frozen
 *   I18N_NS). Prior: Sprint 29 - additive `admin` namespace (minimal admin back-office).
 *   Prior: E2EE i18n namespace (`e2ee` resources)
 * Recent touch (棒 31-225 E3 (a), §11.2, zero FCE): <html lang> follows the UI language (Last contract-affecting Sprint → 本棒,見上).
 * Freeze status: partially_frozen
 * Purpose: i18next initialisation for @kiroku/web.
 *   Supported locales: zh-TW (Traditional Chinese), en (English), ja (Japanese).
 *   zh-CN is reserved for Beta; locale structure and selector already accommodate
 *   future expansion — do NOT add zh-CN resources until Beta sprint.
 *
 *   Detection priority (in order):
 *     1. localStorage key "kiroku_lang"
 *     2. navigator.languages / navigator.language (with normalisation)
 *     3. fallback: en
 *
 *   Normalisation rules:
 *     zh-Hant / zh-HK / zh-TW  → zh-TW
 *     zh-Hans / zh-CN / zh-SG  → zh-TW  (MVP fallback; zh-CN package lands in Beta)
 *     ja*                       → ja
 *     en*                       → en
 *     everything else           → en
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { I18N_NS } from "@kiroku/shared";

// ── Locale list ──────────────────────────────────────────────────────────────

export const SUPPORTED_LOCALES = ["zh-TW", "en", "ja"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  "zh-TW": "繁體中文",
  en: "English",
  ja: "日本語",
};

function isSupportedLocale(v: string): v is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

// ── Language normaliser ───────────────────────────────────────────────────────
//
// Converts raw browser locale codes into one of Kiroku's supported locales.
// Exported so it can be unit-tested independently.

export function normaliseLocale(raw: string): SupportedLocale {
  const lower = raw.toLowerCase().replace(/_/g, "-");

  // Traditional Chinese variants
  if (
    lower === "zh-tw" ||
    lower === "zh-hk" ||
    lower === "zh-mo" ||
    lower.startsWith("zh-hant")
  )
    return "zh-TW";

  // Simplified Chinese variants → MVP fallback to zh-TW until Beta ships zh-CN package
  if (lower.startsWith("zh")) return "zh-TW";

  if (lower.startsWith("ja")) return "ja";
  if (lower.startsWith("en")) return "en";

  return "en";
}

// ── Custom language detector plugin ──────────────────────────────────────────
//
// Standalone detector; does not depend on i18next-browser-languagedetector so
// the normalisation logic is fully explicit and testable.

const KirokuLanguageDetector = {
  type: "languageDetector" as const,
  async: false as const,

  init(): void {},

  detect(): string {
    // 1. Honour explicit user preference stored in localStorage
    const stored = localStorage.getItem("kiroku_lang");
    if (stored && isSupportedLocale(stored)) return stored;

    // 2. Walk browser language list and return the first normalised match
    const browserLangs: string[] = [
      ...(navigator.languages ?? []),
      navigator.language ?? "",
    ].filter(Boolean);

    for (const lang of browserLangs) {
      return normaliseLocale(lang);
    }

    return "en";
  },

  cacheUserLanguage(lng: string): void {
    localStorage.setItem("kiroku_lang", lng);
  },
};

// ── Locale resource imports ───────────────────────────────────────────────────

import zhTWCommon from "@/locales/zh-TW/common.json";
import zhTWAuth from "@/locales/zh-TW/auth.json";
import zhTWNotes from "@/locales/zh-TW/notes.json";
import zhTWErrors from "@/locales/zh-TW/errors.json";
import zhTWEditor from "@/locales/zh-TW/editor.json";
import zhTWSettings from "@/locales/zh-TW/settings.json";
import zhTWNotebooks from "@/locales/zh-TW/notebooks.json";
import zhTWE2ee from "@/locales/zh-TW/e2ee.json";
// Sprint 20 additive: import-export namespace (not added to frozen I18N_NS; use string literal 'import-export')
import zhTWImportExport from "@/locales/zh-TW/import-export.json";
// Sprint 29 additive: admin namespace (not in frozen I18N_NS; string literal 'admin')
import zhTWAdmin from "@/locales/zh-TW/admin.json";
// Sprint 31 批次 2 additive: agenda namespace (not in frozen I18N_NS; string literal 'agenda')
import zhTWAgenda from "@/locales/zh-TW/agenda.json";
// Sprint 34 additive: stamps namespace (calendar スタンプ; string literal 'stamps')
import zhTWStamps from "@/locales/zh-TW/stamps.json";
// Sprint 34 additive: calendar namespace (曆法輪 副曆註記; string literal 'calendar')
import zhTWCalendar from "@/locales/zh-TW/calendar.json";
// Sprint 38 additive: flashcards namespace (建卡 UI; string literal 'flashcards')
import zhTWFlashcards from "@/locales/zh-TW/flashcards.json";

import enCommon from "@/locales/en/common.json";
import enAuth from "@/locales/en/auth.json";
import enNotes from "@/locales/en/notes.json";
import enErrors from "@/locales/en/errors.json";
import enEditor from "@/locales/en/editor.json";
import enSettings from "@/locales/en/settings.json";
import enNotebooks from "@/locales/en/notebooks.json";
import enE2ee from "@/locales/en/e2ee.json";
import enImportExport from "@/locales/en/import-export.json";
import enAdmin from "@/locales/en/admin.json";
import enAgenda from "@/locales/en/agenda.json";
import enStamps from "@/locales/en/stamps.json";
import enCalendar from "@/locales/en/calendar.json";
import enFlashcards from "@/locales/en/flashcards.json";

import jaCommon from "@/locales/ja/common.json";
import jaAuth from "@/locales/ja/auth.json";
import jaNotes from "@/locales/ja/notes.json";
import jaErrors from "@/locales/ja/errors.json";
import jaEditor from "@/locales/ja/editor.json";
import jaSettings from "@/locales/ja/settings.json";
import jaNotebooks from "@/locales/ja/notebooks.json";
import jaE2ee from "@/locales/ja/e2ee.json";
import jaImportExport from "@/locales/ja/import-export.json";
import jaAdmin from "@/locales/ja/admin.json";
import jaAgenda from "@/locales/ja/agenda.json";
import jaStamps from "@/locales/ja/stamps.json";
import jaCalendar from "@/locales/ja/calendar.json";
import jaFlashcards from "@/locales/ja/flashcards.json";

// ── <html lang> follows the UI language (棒 31-225 · OBS-286) ───────────────────
//
// The page's language attribute tracks the language the UI actually renders in:
// i18n.resolvedLanguage (the code the event carries when there is none yet), and
// anything outside SUPPORTED_LOCALES maps to 'en' — the same answer fallbackLng
// gives below. Registered BEFORE init(): the resources are bundled inline, so
// init() resolves synchronously and emits the initial `languageChanged` from inside
// init() — a listener attached after init() would miss it. Every later
// changeLanguage() emits the same event, so the call sites that switch language need
// no change. index.html keeps lang="zh-TW" as the pre-JS value; this takes over.

function syncDocumentLang(lng: string): void {
  if (typeof document === 'undefined') return;
  const current = i18n.resolvedLanguage ?? lng;
  document.documentElement.lang = isSupportedLocale(current) ? current : 'en';
}

i18n.on('languageChanged', syncDocumentLang);

// ── i18next init ──────────────────────────────────────────────────────────────

void i18n
  .use(KirokuLanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      "zh-TW": {
        [I18N_NS.COMMON]: zhTWCommon,
        [I18N_NS.AUTH]: zhTWAuth,
        [I18N_NS.NOTES]: zhTWNotes,
        [I18N_NS.ERRORS]: zhTWErrors,
        [I18N_NS.EDITOR]: zhTWEditor,
        [I18N_NS.SETTINGS]: zhTWSettings,
        [I18N_NS.NOTEBOOKS]: zhTWNotebooks,
        [I18N_NS.E2EE]: zhTWE2ee,
        // Sprint 20 additive: not in frozen I18N_NS; loaded with string literal key
        'import-export': zhTWImportExport,
        // Sprint 29 additive: admin namespace
        'admin': zhTWAdmin,
        // Sprint 31 批次 2 additive: agenda namespace
        'agenda': zhTWAgenda,
        // Sprint 34 additive: stamps namespace
        'stamps': zhTWStamps,
        // Sprint 34 additive: calendar namespace (曆法輪 副曆註記)
        'calendar': zhTWCalendar,
        // Sprint 38 additive: flashcards namespace (建卡 UI)
        'flashcards': zhTWFlashcards,
      },
      en: {
        [I18N_NS.COMMON]: enCommon,
        [I18N_NS.AUTH]: enAuth,
        [I18N_NS.NOTES]: enNotes,
        [I18N_NS.ERRORS]: enErrors,
        [I18N_NS.EDITOR]: enEditor,
        [I18N_NS.SETTINGS]: enSettings,
        [I18N_NS.NOTEBOOKS]: enNotebooks,
        [I18N_NS.E2EE]: enE2ee,
        'import-export': enImportExport,
        'admin': enAdmin,
        'agenda': enAgenda,
        'stamps': enStamps,
        'calendar': enCalendar,
        'flashcards': enFlashcards,
      },
      ja: {
        [I18N_NS.COMMON]: jaCommon,
        [I18N_NS.AUTH]: jaAuth,
        [I18N_NS.NOTES]: jaNotes,
        [I18N_NS.ERRORS]: jaErrors,
        [I18N_NS.EDITOR]: jaEditor,
        [I18N_NS.SETTINGS]: jaSettings,
        [I18N_NS.NOTEBOOKS]: jaNotebooks,
        [I18N_NS.E2EE]: jaE2ee,
        'import-export': jaImportExport,
        'admin': jaAdmin,
        'agenda': jaAgenda,
        'stamps': jaStamps,
        'calendar': jaCalendar,
        'flashcards': jaFlashcards,
      },
      // zh-CN: reserved for Beta — do NOT add resources here until Beta sprint
    },
    fallbackLng: "en",
    defaultNS: I18N_NS.COMMON,
    // Sprint 20 additive: include 'import-export'; Sprint 29 additive: 'admin';
    // Sprint 31 批次 2 additive: 'agenda'; Sprint 34 additive: 'stamps' + 'calendar'
    ns: [...Object.values(I18N_NS), 'import-export', 'admin', 'agenda', 'stamps', 'calendar'],
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
